import {
  useState,
  useEffect,
  useRef,
  type UIEvent,
  type RefObject,
} from 'react';
import OpenNewWindowOutline from '@simplywallst/ui-core/icons/OpenNewWindowOutline';
import type { useCompanyStock } from '../hooks/useCompanyStock';
import { cn } from '@simplywallst/ui-core/next';

export const CompanySummary = ({
  company,
}: {
  company: ReturnType<typeof useCompanyStock>;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [scrollState, setScrollState] = useState({
    ceo: { isScrolledToEnd: false, needsScroll: false },
    website: { isScrolledToEnd: false, needsScroll: false },
  });

  const ceoRef = useRef<HTMLDivElement>(null);
  const websiteRef = useRef<HTMLDivElement>(null);

  const [dragState, setDragState] = useState({
    isDown: false,
    startX: 0,
    scrollLeft: 0,
  });

  const expandDescription = () => {
    setIsExpanded(true);
  };

  const checkScrollState =
    (field: 'ceo' | 'website') => (e: UIEvent<HTMLDivElement>) => {
      const { scrollLeft, clientWidth, scrollWidth } = e.currentTarget;
      const isScrolledToEnd = scrollLeft + clientWidth >= scrollWidth;

      setScrollState((prev) => ({
        ...prev,
        [field]: { ...prev[field], isScrolledToEnd },
      }));
    };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setDragState({
      isDown: true,
      startX: e.pageX - e.currentTarget.offsetLeft,
      scrollLeft: e.currentTarget.scrollLeft,
    });
  };

  const handleDragging = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragState.isDown) return;
    e.preventDefault();
    const x = e.pageX - e.currentTarget.offsetLeft;
    const scrollDistance = (x - dragState.startX) * 2;
    e.currentTarget.scrollLeft = dragState.scrollLeft - scrollDistance;
  };

  const handleMouseUp = () => {
    setDragState((prev) => ({ ...prev, isDown: false }));
  };

  useEffect(() => {
    const checkInitialScroll = (
      ref: RefObject<HTMLDivElement>,
      field: 'ceo' | 'website'
    ) => {
      if (ref.current) {
        const { clientWidth, scrollWidth } = ref.current;
        const needsScroll = scrollWidth > clientWidth;
        setScrollState((prev) => ({
          ...prev,
          [field]: { ...prev[field], needsScroll },
        }));
      }
    };

    checkInitialScroll(ceoRef, 'ceo');
    checkInitialScroll(websiteRef, 'website');
  }, [company.ceoName, company.url]);

  return (
    <div
      style={
        {
          '--gradient-color': 'var(--s-color-surface-1)',
        } as React.CSSProperties
      }
    >
      <dl className="grid grid-cols-[auto_auto] grid-rows-[auto_auto_auto_auto] gap-x-4 text-sm">
        <dt className="col-start-1 row-start-1 self-end text-xs text-soft">
          Founded
        </dt>
        <dd className="col-start-1 row-start-2 m-0 text-lg font-[500]">
          {company.yearFunded ?? 'n/a'}
        </dd>

        <dt className="col-start-2 row-start-1 self-end text-xs text-soft">
          Employees
        </dt>
        <dd className="col-start-2 row-start-2 m-0 text-lg font-[500]">
          {company.totalEmployees ?? 'n/a'}
        </dd>

        <dt className="col-start-1 row-start-3 mt-2 self-end text-xs text-soft">
          CEO
        </dt>
        <dd className="relative col-start-1 row-start-4 m-0 overflow-x-auto">
          <div
            ref={ceoRef}
            className={cn(
              'select-none overflow-x-auto scroll-smooth whitespace-nowrap text-lg font-[500] [scrollbar-width:none]',
              scrollState.ceo.needsScroll &&
                'cursor-grab active:cursor-grabbing'
            )}
            onScroll={checkScrollState('ceo')}
            onMouseDown={handleMouseDown}
            onMouseMove={handleDragging}
            onMouseUp={handleMouseUp}
          >
            {company.ceoName ?? 'n/a'}
          </div>
          {scrollState.ceo.needsScroll && !scrollState.ceo.isScrolledToEnd && (
            <div className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-[linear-gradient(to_right,transparent,var(--gradient-color))]" />
          )}
        </dd>

        <dt className="col-start-2 row-start-3 self-end text-xs text-soft">
          Website
          {company.url && (
            <a
              href={`https://${company.url}`}
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              <OpenNewWindowOutline className="ml-1 inline-block size-5 fill-p-blue-100 align-text-bottom" />
              <span className="sr-only">View website</span>
            </a>
          )}
        </dt>
        <dd className="relative col-start-2 row-start-4 m-0 overflow-x-auto">
          <div
            ref={websiteRef}
            className={cn(
              'select-none overflow-x-auto scroll-smooth whitespace-nowrap text-lg font-[500] [scrollbar-width:none]',
              scrollState.website.needsScroll &&
                'cursor-grab active:cursor-grabbing'
            )}
            onScroll={checkScrollState('website')}
            onMouseDown={handleMouseDown}
            onMouseMove={handleDragging}
            onMouseUp={handleMouseUp}
          >
            {company.url ?? 'n/a'}
          </div>
          {scrollState.website.needsScroll &&
            !scrollState.website.isScrolledToEnd && (
              <div className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-[linear-gradient(to_right,transparent,var(--gradient-color))]" />
            )}
        </dd>
      </dl>

      <div className="relative mt-6">
        {!isExpanded && (
          <button
            onClick={expandDescription}
            className="absolute inset-0 bg-transparent focus:outline-dashed focus:outline-1 focus:outline-offset-[3px] focus:outline-p-blue-100"
            aria-expanded={isExpanded}
            aria-label="Read more"
          />
        )}
        <p
          className={cn(
            'z-10 m-0 text-sm transition-all duration-500 ease-in-out',
            isExpanded
              ? 'scrollbar max-h-56 overflow-y-auto'
              : 'max-h-20 overflow-hidden'
          )}
          onClick={expandDescription}
        >
          {company.description}
        </p>

        {!isExpanded && (
          <div className="pointer-events-none absolute bottom-0 h-12 w-full bg-[linear-gradient(to_bottom,transparent,var(--gradient-color))]" />
        )}
      </div>
    </div>
  );
};
